import { statuses } from '../../../Data/Jobs';
import { durationToSeconds, plural } from '../../../Services/helpers';
import { executionModes, triggerTypes } from '../../../Data/Journal';

export const DEFAULT_DAILY_TRACKER_GROUP_BY = 'assignedTo';
export const DEFAULT_DAILY_TRACKER_ROW_END_METRIC = 'jobs';

const DATE_STATUS = {
    EMPTY: '-',
    PLANNED: 'Planned',
    IN_PROGRESS: 'In Progress',
    COMPLETE: 'Complete',
    INCOMPLETE: 'Incomplete',
    SUGGESTED: 'Suggested',
    BLOCKED: 'Blocked',
    TRIGGERS: 'Notifications',
};

const getGroupId = (job, section, groupBy) => {
    if (groupBy === 'roundId') {
        return job.roundId ?? 'unassigned';
    }

    if (section === 'suggested') {
        return job.suggestedAssignedTo ?? job.assignedTo ?? 'unassigned';
    }

    return job.assignedTo ?? job.suggestedAssignedTo ?? 'unassigned';
};

const remapJobStatus = (status) => {
    if (status === 'Upcoming') {
        return 'Done';
    }

    return status;
};

const jobStatus = (job) => {
    return remapJobStatus(job?.relevantOccurrence?.status || job.status || 'Unknown');
};

const jobCostPence = (job) => {
    if (job.relevantOccurrence?.adjustedCost) {
        return parseInt(job.relevantOccurrence.adjustedCost, 10);
    }

    const jobCost = parseInt(job?.cost ?? 0, 10);
    const adjustment = parseInt(job?.itemisationAdjustment?.amount ?? 0, 10);

    return jobCost + adjustment;
};

const addJobToStatusMap = (jobsByStatus, job) => {
    const status = jobStatus(job);

    if (!jobsByStatus[status]) {
        jobsByStatus[status] = [];
    }

    jobsByStatus[status].push(job);
};

const buildGroupTotals = (jobsByStatus) => {
    let totalTime = 0;
    let totalCost = 0;
    let done = 0;
    let total = 0;

    Object.entries(jobsByStatus).forEach(([status, jobs]) => {
        const statusConfig = statuses[status];

        jobs.forEach(job => {
            total++;
            totalTime += durationToSeconds(job.duration ?? '00:15:00');
            totalCost += jobCostPence(job);

            if (statusConfig?.considerDone) {
                done++;
            }
        });
    });

    return { totalTime, totalCost, done, total };
};

const filterRelevantTriggers = (triggers, date, today, tomorrow) => {
    return (triggers ?? []).filter(
        t => t.executionMode === executionModes.MANUAL || t.executionMode === executionModes.SCHEDULED,
    ).filter(t => {
        if (t.type === triggerTypes.JOB_DUE_TODAY && date !== today) {
            return false;
        }

        if (t.type === triggerTypes.JOB_DUE_TOMORROW && date !== tomorrow) {
            return false;
        }

        if (t.availableCount === 0 && t.executedCount === 0) {
            return false;
        }

        return true;
    });
};

const pendingTriggerCount = (triggers) => {
    return triggers.reduce((acc, t) => acc + t.availableCount, 0);
};

const resolveDateStatus = ({ date, today, totalJobs, doneJobs, hasSuggestedJobs, isBlocked, pendingTriggers }) => {
    if (totalJobs === 0 && isBlocked) {
        return DATE_STATUS.BLOCKED;
    }

    if (totalJobs === 0) {
        return hasSuggestedJobs ? DATE_STATUS.SUGGESTED : DATE_STATUS.EMPTY;
    }

    if (pendingTriggers > 0) {
        return DATE_STATUS.TRIGGERS;
    }

    if (date > today) {
        return DATE_STATUS.PLANNED;
    }

    if (date === today) {
        return doneJobs < totalJobs ? DATE_STATUS.IN_PROGRESS : DATE_STATUS.COMPLETE;
    }

    return doneJobs === totalJobs ? DATE_STATUS.COMPLETE : DATE_STATUS.INCOMPLETE;
};

export const getDateStatusConfig = (dateStatus) => {
    const configMap = {
        [DATE_STATUS.COMPLETE]: {
            theme: 'success',
            icon: 'Check',
            textColor: 'text-green-600 fill-green-600',
            bgColor: 'bg-green-100',
        },
        [DATE_STATUS.IN_PROGRESS]: {
            theme: 'primary',
            icon: 'Play',
            textColor: 'text-blue-600 fill-blue-600',
            bgColor: 'bg-blue-100',
        },
        [DATE_STATUS.PLANNED]: {
            theme: 'info',
            icon: 'CalendarClock',
            textColor: 'text-gray-600 fill-gray-600',
            bgColor: 'bg-gray-100',
        },
        [DATE_STATUS.INCOMPLETE]: {
            theme: 'danger',
            icon: 'TriangleExclamation',
            textColor: 'text-red-600 fill-red-600',
            bgColor: 'bg-red-100',
        },
        [DATE_STATUS.EMPTY]: {
            theme: 'secondary',
            icon: 'Circle',
            textColor: 'text-muted fill-muted',
            bgColor: 'bg-gray-50',
        },
        [DATE_STATUS.SUGGESTED]: {
            theme: 'warning',
            icon: 'LightbulbOn',
            textColor: 'text-yellow-600 fill-yellow-600',
            bgColor: 'bg-yellow-100',
        },
        [DATE_STATUS.BLOCKED]: {
            theme: 'secondary',
            icon: 'Ban',
            textColor: 'text-muted fill-muted',
            bgColor: 'bg-gray-100',
        },
        [DATE_STATUS.TRIGGERS]: {
            theme: 'info',
            icon: 'PaperPlane',
            textColor: 'text-blue-600 fill-blue-600',
            bgColor: 'bg-blue-100',
        },
    };

    return configMap[dateStatus] ?? configMap[DATE_STATUS.EMPTY];
};

const buildDayGroup = (groupId, isWorkerBlocked = false) => ({
    groupId,
    isWorkerBlocked,
    jobsByStatus: {},
    suggestedJobs: [],
    totals: { totalTime: 0, totalCost: 0, done: 0, total: 0 },
});

const workerBlockedSet = (workerBlocks) => new Set(workerBlocks ?? []);

const isGroupWorkerBlocked = (groupId, workerBlocks, groupBy) => {
    if (groupBy !== 'assignedTo' || groupId === 'unassigned') {
        return false;
    }

    return workerBlockedSet(workerBlocks).has(groupId);
};

const ensureGroup = (groups, groupId, workerBlocks, groupBy) => {
    if (!groups[groupId]) {
        groups[groupId] = buildDayGroup(groupId, isGroupWorkerBlocked(groupId, workerBlocks, groupBy));
    }

    return groups[groupId];
};

const buildDay = (date, day, today, tomorrow, groupBy) => {
    const groups = {};
    const workerBlocks = day.workerBlocks ?? [];
    let hasSuggestedJobs = false;

    day.scheduledJobs?.forEach(job => {
        const group = ensureGroup(groups, getGroupId(job, 'scheduled', groupBy), workerBlocks, groupBy);
        addJobToStatusMap(group.jobsByStatus, job);
    });

    day.suggestedJobs?.forEach(job => {
        hasSuggestedJobs = true;
        const group = ensureGroup(groups, getGroupId(job, 'suggested', groupBy), workerBlocks, groupBy);
        group.suggestedJobs.push(job);
    });

    Object.values(groups).forEach(group => {
        group.totals = buildGroupTotals(group.jobsByStatus);
    });

    let totalJobs = 0;
    let doneJobs = 0;
    let totalCost = 0;

    Object.values(groups).forEach(group => {
        totalJobs += group.totals.total;
        doneJobs += group.totals.done;
        totalCost += group.totals.totalCost;
    });

    const triggers = filterRelevantTriggers(day.triggers, date, today, tomorrow);
    const pendingTriggers = pendingTriggerCount(triggers);
    const dateStatus = resolveDateStatus({
        date,
        today,
        totalJobs,
        doneJobs,
        hasSuggestedJobs,
        isBlocked: day.isBlocked,
        pendingTriggers,
    });

    return {
        date,
        isBlocked: day.isBlocked,
        workerBlocks,
        groups,
        triggers,
        totalJobs,
        doneJobs,
        totalCost,
        dateStatus,
        statusConfig: getDateStatusConfig(dateStatus),
        hasSuggestedJobs,
    };
};

export const buildWeekOverviewDays = (overview, groupBy = DEFAULT_DAILY_TRACKER_GROUP_BY) => {
    if (!overview?.dates) {
        return null;
    }

    const today = overview.today;
    const tomorrow = overview.tomorrow;

    return Object.keys(overview.dates)
        .sort()
        .map(date => buildDay(date, overview.dates[date], today, tomorrow, groupBy));
};

export const sortedStatusesForGroup = (jobsByStatus) => {
    return Object.entries(jobsByStatus)
        .map(([status, jobs]) => ({
            status,
            jobs,
            totalDuration: jobs.reduce((sum, job) => sum + durationToSeconds(job.duration ?? '00:15:00'), 0),
        }))
        .filter(item => item.jobs.length > 0)
        .sort((a, b) => b.totalDuration - a.totalDuration);
};

export const sumJobsCostPence = (jobs) => {
    return jobs.reduce((sum, job) => sum + jobCostPence(job), 0);
};

const scheduleAssignToFromJobs = (jobs) => {
    const counts = {};

    jobs.forEach(job => {
        const id = job.suggestedAssignedTo ?? job.assignedTo ?? 'unassigned';
        counts[id] = (counts[id] ?? 0) + 1;
    });

    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);

    return sorted[0]?.[0] ?? 'unassigned';
};

export const buildScheduleModalPayload = (suggestedJobs, groupBy, groupId, roundsById) => {
    if (groupBy === 'roundId') {
        if (groupId === 'unassigned') {
            return {
                rounds: [],
                jobs: suggestedJobs,
                assignTo: scheduleAssignToFromJobs(suggestedJobs),
            };
        }

        return {
            rounds: [{
                id: groupId,
                name: roundsById[groupId]?.name ?? 'Round',
                jobs: suggestedJobs,
            }],
            jobs: [],
            assignTo: scheduleAssignToFromJobs(suggestedJobs),
        };
    }

    const roundsMap = {};
    const jobsWithoutRound = [];

    suggestedJobs.forEach(job => {
        if (job.roundId) {
            if (!roundsMap[job.roundId]) {
                roundsMap[job.roundId] = {
                    id: job.roundId,
                    name: roundsById[job.roundId]?.name ?? 'Round',
                    jobs: [],
                };
            }

            roundsMap[job.roundId].jobs.push(job);
        } else {
            jobsWithoutRound.push(job);
        }
    });

    return {
        rounds: Object.values(roundsMap),
        jobs: jobsWithoutRound,
        assignTo: groupId === 'unassigned' ? 'unassigned' : groupId,
    };
};

export const groupRowJobsLabel = (totals) => {
    if (totals.total === 0) {
        return null;
    }

    const totalPlural = plural(totals.total, '', ' job', ' jobs');

    if (totals.done > 0) {
        return `${totals.done}/${totals.total}${totalPlural}`;
    }

    return `${totals.total}${totalPlural}`;
};
